// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: rentalBalances/$walletAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Escrow LEDGER of each renter's deposited SOL for the GPU rental marketplace. Doc id is the renter's wallet address. Tracks the spendable balance (in lamports) that the backend debits as rentals accrue. The backend (PROJECT_VAULT_ADDRESS) is the SOLE authority over this ledger — all balance mutations happen server-side. Native SOL only (lamports). No token transfer happens here: SOL actually enters the treasury via rentalDeposits (deposit) and leaves via rentalPayouts (settle/withdraw); this collection is purely the offchain accounting ledger.
 */

export interface RentalBalancesRequest {
  owner: AddressType;
  balanceLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  totalDeposited: number | TimeOperation | IncrementOperation | TokenAmount;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface RentalBalancesResponse {
  owner: string;
  balanceLamports: number;
  totalDeposited: number;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a RentalBalances operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRentalBalances(walletAddress: string, data: RentalBalancesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentalBalances/${walletAddress}`, document: doc };
}

/**
 * BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS. The backend creates the ledger doc (id = renter wallet address) after a rentalDeposits transfer lands, setting owner to the renter, balanceLamports and totalDeposited to the credited lamports, and updatedAt to server time (unix seconds). balanceLamports/totalDeposited are required UInt (never optional) to avoid null arithmetic; the backend always supplies them. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRentalBalances(walletAddress: string, data: RentalBalancesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentalBalances/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting RentalBalances: ${formatError(error)}`);
    return false;
  }
}

export type RentalBalancesRequestUpdate = Partial<RentalBalancesRequest>;

/**
 * Build a RentalBalances update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateRentalBalances(walletAddress: string, data: RentalBalancesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentalBalances/${walletAddress}`, document: doc };
}

/**
 * BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS. The backend increments balanceLamports/totalDeposited on new deposits, and decrements balanceLamports as rentals settle or on withdrawal. Renters CANNOT mutate their own balance (that would allow free GPU time). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateRentalBalances(walletAddress: string, data: RentalBalancesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentalBalances/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating RentalBalances: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
   (Get Single Item)
 */
export async function getRentalBalances(walletAddress: string): Promise<RentalBalancesResponse | null> {
  try {
    const result = await get(`rentalBalances/${walletAddress}`);
    return result as RentalBalancesResponse | null;
  } catch (error) {
    console.error(`Error getting RentalBalances: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single RentalBalances document. (
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
  )
 */
export function subscribeRentalBalances(callback: (data: RentalBalancesResponse | null) => void, walletAddress: string): Promise<() => Promise<void>> {
  return subscribe(`rentalBalances/${walletAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RentalBalancesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to RentalBalances at path '${`rentalBalances/${walletAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many RentalBalances items from collection rentalBalances
 
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
  
 */
export async function getManyRentalBalances(filter: string = ""): Promise<RentalBalancesResponse[]> {
  try {
    const results = await get("rentalBalances", { prompt: filter });
    return Array.isArray(results) ? results as RentalBalancesResponse[] : [];
  } catch (error) {
    console.error(`Error getting RentalBalances collection at path 'rentalBalances': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RentalBalances collection at rentalBalances
 
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
  
 */
export function subscribeManyRentalBalances(callback: (data: RentalBalancesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentalBalances", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalBalancesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RentalBalances collection at path '${"rentalBalances"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all RentalBalances items from collection rentalBalances
 
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
  
 */
export async function getAllRentalBalances(filter: string = ""): Promise<RentalBalancesResponse[]> {
  try {
    const results = await get("rentalBalances", { prompt: filter });
    return Array.isArray(results) ? results as RentalBalancesResponse[] : [];
  } catch (error) {
    console.error(`Error getting RentalBalances collection at path 'rentalBalances': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RentalBalances collection at rentalBalances
 
  Read Operation Details: Anyone can read rental ledger balances (owner, balanceLamports, totalDeposited, updatedAt). Public so dashboards can show a renter's available balance (display-only on the frontend).
  
 */
export function subscribeAllRentalBalances(callback: (data: RentalBalancesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentalBalances", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalBalancesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RentalBalances collection at path '${"rentalBalances"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count RentalBalances items in collection rentalBalances.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRentalBalances(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("rentalBalances", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting RentalBalances at path 'rentalBalances': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on RentalBalances items in collection rentalBalances.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRentalBalances(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("rentalBalances", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating RentalBalances at path 'rentalBalances': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteRentalBalances(walletAddress: string): Promise<boolean> {
  try {
    const succeeded = await set(`rentalBalances/${walletAddress}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting RentalBalances: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for RentalBalances for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteRentalBalances(walletAddress: string): DocumentOperation {
  return { path: `rentalBalances/${walletAddress}`, document: null };
}

